import cx from "classnames";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { Models } from "@/api/types";
import { useTranslation } from "@/core/i18n";
import {
  CURRENT_ORDER_QUERY_KEY,
  invalidateCurrentOrderDetails,
} from "@/modules/cart/useCartDetails";
import { adPixels } from "@/modules/analytics/AdPixels/adPixels";
import { LocationListPickerV3 } from "@/modules/onboarding/components/LocationListPickerV3/LocationListPickerV3";
import { transformLocationsByCity } from "@/modules/onboarding/utils/locationTransform";
import TopNav from "@/molecules/navbars/TopNav";
import { queryOptions } from "@tanstack/react-query";

import { queryClient } from "@/pages/Root";
import { getAuthorizationHeader, rawPostText } from "api";
import {
  getListingsById,
  getMultiPlatformPlatformByPlatformIdLocations,
  getOrdersCurrentDetails,
  getOrdersCurrentLineitems,
  getOrdersCurrentSummary,
  getPartnerEmrTherapists,
  getPlatform,
  getPlatformLocation,
  getPlatformLocationByLocationId,
  getUser,
  postOrdersCurrentLineitems,
  postPlatformLocationByLocationIdBook,
} from "api/methods";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronDown from "atoms/icon/icons/chevron_down_small.svg";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";

import BookingCalendar from "./BookingCalendar";
import BookingProvidersList from "./BookingProvidersList";

const load = async (id: string, locationId: string | null) => {
  let platformLocation;

  const platform = await queryClient.fetchQuery({ ...getPlatform.keyed() });

  const platformLocations = await queryClient.fetchQuery({
    ...getMultiPlatformPlatformByPlatformIdLocations.keyed({
      platformId: platform.id,
    }),
  });

  const user = await queryClient.fetchQuery({ ...getUser.keyed() });

  if (locationId) {
    platformLocation = await queryClient.fetchQuery({
      ...getPlatformLocationByLocationId.keyed({ locationId }),
    });
  } else if (user?.platformLocation?.id) {
    const userLocationId = user.platformLocation.id;
    platformLocation = await queryClient.fetchQuery({
      ...getPlatformLocationByLocationId.keyed({ locationId: userLocationId }),
    });
  }

  platformLocation ??= await queryClient.fetchQuery({
    ...getPlatformLocation.keyed(),
    staleTime: 1000 * 60 * 10,
  });

  const listing = await queryClient.fetchQuery({
    ...getListingsById.keyed({ id }),
    staleTime: 1000 * 60 * 10,
  });

  const therapists = await queryClient.fetchQuery(
    queryOptions({
      queryKey: getPartnerEmrTherapists.key({
        ExternalPlatformLocationId: platformLocation.zenotiId ?? undefined,
        ServiceId: listing.partnerEmrId ?? undefined,
      }),
      queryFn: () => {
        if (!platformLocation.zenotiId || !listing.partnerEmrId) {
          throw new Error("Missing required platform location or service ID");
        }
        return getPartnerEmrTherapists({
          ExternalPlatformLocationId: platformLocation.zenotiId,
          ServiceId: listing.partnerEmrId,
        });
      },
      staleTime: 1000 * 60 * 10,
    }),
  );

  return { therapists, listing, platformLocation, platformLocations, user };
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const bookingProvidersLoader: LoaderFunction = ({ params: { id }, request }) => {
  if (!id) return redirect("/");
  const url = new URL(request.url);
  const locationId = url.searchParams.get("locationId");
  return load(id, locationId);
};

const mapTherapistsToProviders = (therapists: unknown): Partial<Models<"DisplayProvider">>[] => {
  // Handle case where response might be wrapped in result or direct
  const data =
    typeof therapists === "object" &&
    therapists !== null &&
    "result" in therapists &&
    therapists.result !== null
      ? therapists.result
      : therapists;

  // Type guard to check if data has Therapists array
  if (
    typeof data === "object" &&
    data !== null &&
    "Therapists" in data &&
    Array.isArray(data.Therapists)
  ) {
    return data.Therapists.map((therapist: unknown): Partial<Models<"DisplayProvider">> => {
      if (
        typeof therapist === "object" &&
        therapist !== null &&
        "Id" in therapist &&
        "Name" in therapist &&
        typeof therapist.Id === "string" &&
        typeof therapist.Name === "string"
      ) {
        return {
          id: therapist.Id,
          displayName: therapist.Name,
        };
      }
      return {};
    }).filter((provider) => provider.id !== undefined);
  }

  return [];
};

const BookingProviders = () => {
  const { t } = useTranslation();
  const {
    therapists: initialTherapists,
    listing,
    platformLocation: initialPlatformLocation,
    platformLocations,
  } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Extract query parameters
  const packageId = searchParams.get("packageId");
  const priceId = searchParams.get("priceId");
  const membershipId = searchParams.get("membershipId");

  const [selectedProviderId, setSelectedProviderId] = useState<string | null | undefined>(
    undefined,
  );
  const [showCalendar, setShowCalendar] = useState(false);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  // Track the latest date request to prevent race conditions
  const latestDateRequestRef = useRef<number | null>(null);

  // State for location picker expansion
  const [isLocationPickerExpanded, setIsLocationPickerExpanded] = useState(false);

  // State for selected location and therapists
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    initialPlatformLocation?.id || null,
  );
  const [platformLocation, setPlatformLocation] = useState(initialPlatformLocation);
  const [therapists, setTherapists] = useState(initialTherapists);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);

  const locations = platformLocations?.locations || [];

  // Current selected location
  const currentLocationInList = locations.find((l: any) => l.id === selectedLocationId);

  const providers = useMemo(() => mapTherapistsToProviders(therapists), [therapists]);

  const cityGroups = useMemo(() => {
    const formattedLocations = locations.map((l: any) => ({
      ...l,
      city: l.state ? `${l.city}, ${l.state}` : l.city,
    }));
    return transformLocationsByCity(formattedLocations);
  }, [locations]);

  const handleLocationSelect = async (locationId: string) => {
    if (locationId === currentLocationInList?.id) {
      return;
    }

    // Update the selected location
    setSelectedLocationId(locationId);
    setIsLoadingProviders(true);

    try {
      const newPlatformLocation = await queryClient.fetchQuery({
        ...getPlatformLocationByLocationId.keyed({ locationId }),
      });

      setPlatformLocation(newPlatformLocation);

      const newTherapists = await queryClient.fetchQuery({
        queryKey: getPartnerEmrTherapists.key({
          ExternalPlatformLocationId: newPlatformLocation.zenotiId ?? undefined,
          ServiceId: listing.partnerEmrId ?? undefined,
        }),
        queryFn: () => {
          if (!newPlatformLocation.zenotiId || !listing.partnerEmrId) {
            throw new Error("Missing required platform location or service ID");
          }
          return getPartnerEmrTherapists({
            ExternalPlatformLocationId: newPlatformLocation.zenotiId,
            ServiceId: listing.partnerEmrId,
          });
        },
      });

      setTherapists(newTherapists);
      // Reset provider selection when location changes
      setSelectedProviderId(undefined);
      setShowCalendar(false);
    } catch (error) {
      console.error("Error fetching providers for new location:", error);
    } finally {
      setIsLoadingProviders(false);
    }
  };

  const handleDateSelection = async (date: Date | undefined) => {
    setSelectedDate(date);

    if (!date) {
      latestDateRequestRef.current = null;
      return;
    }

    // Create a unique identifier for this request using timestamp
    const requestId = Date.now();
    latestDateRequestRef.current = requestId;

    setIsLoadingTimeSlots(true);
    try {
      const responses = await fetchProviderSchedules(date, false, selectedProviderId);

      // Check if this is still the latest request before updating state
      if (latestDateRequestRef.current !== requestId) {
        // This request is stale, ignore the response
        return;
      }

      if (!responses) {
        throw new Error("Failed to fetch provider schedules");
      }

      // Responses are already parsed as objects
      const schedulesFromResponse = responses.map((data) => {
        const openSlots = data.OpenSlots || [];
        const selectedService = data.SlotBookings[0].Services.find(
          (service: { Service: { Id: string } }) => service.Service.Id === listing.partnerEmrId,
        );
        return openSlots.map(
          (day: { Time: string; Available: boolean; HolidayType: string | null }) => ({
            date: dayjs(day.Time).format("YYYY-MM-DD"),
            startTime: day.Time,
            endTime: dayjs(day.Time)
              .add(selectedService.Service.Duration, "minutes")
              .format("YYYY-MM-DDTHH:mm:ss"),
            isAvailable: day.Available,
          }),
        );
      });

      // Double-check the request is still current before updating
      if (latestDateRequestRef.current !== requestId) {
        return;
      }

      const updatedSchedules = providerSchedules.filter(
        (schedule) => schedule.date !== dayjs(date).format("YYYY-MM-DD"),
      );

      setProviderSchedules([...schedulesFromResponse[0], ...updatedSchedules]);
      scrollToBottom();
    } catch (error) {
      // Only log errors for the latest request
      if (latestDateRequestRef.current === requestId) {
        console.error("Error fetching time slots:", error);
      }
    } finally {
      // Only update loading state if this is still the latest request
      if (latestDateRequestRef.current === requestId) {
        setIsLoadingTimeSlots(false);
      }
    }
  };
  const [selectedTime, setSelectedTime] = useState<string | undefined>(undefined);
  const [providerSchedules, setProviderSchedules] = useState<
    Models<"DisplayProviderAvailabilitySlot">[]
  >([]);
  const [isBooking, setIsBooking] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  // Use useEffect to handle navigation instead of returning redirect
  useEffect(() => {
    if (!packageId || !priceId) {
      navigate("..");
    }
  }, [packageId, priceId, navigate]);

  // Auto-select location if there's only one available
  useEffect(() => {
    if (locations.length === 1 && locations[0].id !== selectedLocationId) {
      handleLocationSelect(locations[0].id);
    }
  }, [locations]);

  // Early return for loading state or invalid params
  if (!packageId || !priceId) {
    return null; // or a loading spinner
  }

  const fetchProviderSchedules = async (
    startDate: Date,
    includeFutureDays = true,
    providerIdOverride?: string | null,
  ) => {
    try {
      // Create dates for 4 requests: today, +7 days, +14 days, +21 days
      const dates = includeFutureDays
        ? [0, 7, 14, 21, 28].map((days) =>
            dayjs(startDate).add(days, "day").format("YYYY-MM-DD HH:mm:ss"),
          )
        : [dayjs(startDate).format("YYYY-MM-DD HH:mm:ss")];

      // Validate required fields
      if (!platformLocation.zenotiId || !listing.partnerEmrId) {
        throw new Error("Missing required platform location or service ID");
      }

      const externalPlatformLocationId = platformLocation.zenotiId;
      const serviceId = listing.partnerEmrId;

      const actualProviderId = providerIdOverride ?? selectedProviderId;

      // Create array of fetch promises for parallel execution
      // Use axios directly with responseType: 'text' to force string response
      // which the post function can handle
      const fetchPromises = dates.map((centerDate) => {
        const payload = {
          externalPlatformLocationId,
          serviceId,
          requestedAppointmentDate: dayjs(centerDate).format("YYYY-MM-DD"),
          ...(actualProviderId && {
            selectedWellnessProviderExternalId: actualProviderId,
          }),
        };

        return rawPostText("/partner-emr/appointments/available-times", payload, {
          "Content-Type": "application/json",
          "Authorization": getAuthorizationHeader(),
        });
      });

      // Execute all requests in parallel
      const responses = await Promise.all(fetchPromises);

      // Parse string responses to objects
      return responses.map((response) => {
        if (typeof response === "string") {
          return JSON.parse(response);
        }
        return response;
      });
    } catch (error) {
      console.error("Error fetching provider schedules:", error);
    }
  };

  const handleProviderSelection = async (providerId: string | null) => {
    setSelectedProviderId(providerId);

    try {
      const responses = await fetchProviderSchedules(new Date(), true, providerId);
      if (!responses) {
        throw new Error("Failed to fetch provider schedules");
      }

      // Extract FutureDays from responses (already parsed as objects)
      const schedulesFromResponse = responses.map((data) => {
        const futureDays = data.FutureDays || [];

        // Transform each day: rename Day to date, remove HolidayType, rename IsAvailable to isAvailable
        return futureDays
          .filter((day: { IsAvailable: boolean }) => day.IsAvailable)
          .map((day: { Day: string; IsAvailable: boolean; HolidayType: string | null }) => ({
            date: dayjs(day.Day).format("YYYY-MM-DD"),
            isAvailable: day.IsAvailable,
          }));
      });

      // Combine all schedules from all responses
      const allSchedules = schedulesFromResponse.flat();

      setProviderSchedules(allSchedules);

      // Only show calendar after getting the result
      setShowCalendar(true);
    } catch (error) {
      console.error("Error fetching provider schedules:", error);
      setProviderSchedules([]);
      // Still show calendar even if there's an error
      setShowCalendar(true);
    }
  };

  const handleBackToProviders = () => {
    setShowCalendar(false);
    setSelectedDate(undefined);
    setSelectedTime(undefined);
  };

  const getSelectedProvider = () => {
    if (!selectedProviderId || !Array.isArray(providers)) return null;
    return providers.find((provider) => provider.id === selectedProviderId) || null;
  };

  const handleBookingError = (errorMessage: string) => {
    console.error("Booking request failed: ", errorMessage);
    setIsBooking(false);
    setSelectedTime(undefined);
    scrollToBottom();
  };

  const handleBook = async () => {
    if (!selectedDate || !selectedTime || !listing) return;

    // Validate that platform location is available before proceeding
    if (!platformLocation?.id) {
      handleBookingError(t("bookingProviders.errorLocationMissing"));
      return;
    }

    setIsBooking(true);

    try {
      // Create the booking request
      const [hours, minutes] = selectedTime.split(":").map(Number);
      const selectedDateTime = new Date(selectedDate);
      selectedDateTime.setHours(hours, minutes, 0, 0);
      const bookingRequest: Models<"CreateBookingRequest"> = {
        platformLocationId: platformLocation.id,
        items: [
          {
            packageId,
            startTimeUtc: `${dayjs(selectedDateTime).format("YYYY-MM-DDTHH:mm:ss")}Z`,
            ...(selectedProviderId && {
              externalProviderId: selectedProviderId.toString(),
            }),
          },
        ],
      };

      // Make the booking request
      let bookingResponse;
      try {
        bookingResponse = await postPlatformLocationByLocationIdBook({
          locationId: platformLocation.id,
          ...bookingRequest,
        });
      } catch {
        handleBookingError(t("bookingProviders.errorBookingFailedContactSupport"));
        return;
      }

      if (!bookingResponse?.isReserved) {
        handleBookingError(
          bookingResponse?.errorMessage || t("bookingProviders.errorBookingFailed"),
        );
        return;
      }

      const appointmentId = bookingResponse?.appointmentId;

      // Validate appointment ID was returned
      if (!appointmentId) {
        handleBookingError(t("bookingProviders.errorNoAppointmentId"));
        return;
      }

      await postOrdersCurrentLineitems({
        packageListingId: listing.id,
        packageId: packageId,
        packagePriceId: priceId,
        ...(membershipId && { membershipId }),
        ...(appointmentId && { appointmentId }),
      });
      adPixels.schedule({ id: listing.id, name: listing.title });

      // Invalidate and refetch cart queries to ensure fresh data
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getOrdersCurrentSummary.key({}),
        }),
        invalidateCurrentOrderDetails(),
        queryClient.invalidateQueries({
          queryKey: getOrdersCurrentLineitems.key(),
        }),
      ]);

      await Promise.all([
        queryClient.fetchQuery({
          ...getOrdersCurrentSummary.keyed({ includePackageLineItems: true }),
        }),
        queryClient.fetchQuery({
          queryKey: CURRENT_ORDER_QUERY_KEY,
          queryFn: () => getOrdersCurrentDetails(),
        }),
        queryClient.fetchQuery({ ...getOrdersCurrentLineitems.keyed() }),
      ]);

      navigate("/cart");
    } catch (error) {
      console.error("Booking failed:", error);
      setIsBooking(false);
    }
  };

  return (
    <div className="flex h-screen flex-col">
      <TopNav
        startIconSVG={ChevronLeft}
        onStartIconClick={() => {
          if (showCalendar) {
            setShowCalendar(false);
            setSelectedProviderId(undefined);
            setSelectedDate(undefined);
            setSelectedTime(undefined);
            setProviderSchedules([]);
          } else {
            navigate(-1);
          }
        }}
      >
        <div className="text-center text-lg font-medium text-gray-800">
          {showCalendar ? t("bookingProviders.selectTime") : t("bookingProviders.selectProvider")}
        </div>
      </TopNav>

      <div className="flex-1 overflow-y-auto">
        {showCalendar ? (
          <div className="p-4">
            <div className="mt-8 text-center text-sub1 font-medium uppercase text-brand-color">
              {t("bookingProviders.selectATime")}
            </div>

            {/* Calendar and Time Slot Selection */}
            <BookingCalendar
              providerName={getSelectedProvider()?.displayName || null}
              onBackToProviders={handleBackToProviders}
              providerSchedules={providerSchedules}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              setSelectedDate={handleDateSelection}
              setSelectedTime={setSelectedTime}
              isLoadingTimeSlots={isLoadingTimeSlots}
            />
          </div>
        ) : (
          <div className="p-4">
            <div className="mt-8 text-center text-sub1 font-medium uppercase text-brand-color">
              {t("bookingProviders.chooseYourProvider")}
            </div>
            {currentLocationInList && locations.length > 1 && (
              <button
                onClick={() => setIsLocationPickerExpanded(!isLocationPickerExpanded)}
                className="mt-1 flex w-full items-center justify-center gap-1 text-center text-base font-bold text-gray-800"
              >
                {currentLocationInList.name}
                <Icon
                  svg={ChevronDown}
                  size="small"
                  className={cx("transition-transform duration-200", {
                    "rotate-180": isLocationPickerExpanded,
                  })}
                />
              </button>
            )}
            {currentLocationInList && locations.length === 1 && (
              <div className="mt-1 text-center text-base font-bold text-gray-800">
                {currentLocationInList.name}
              </div>
            )}

            {isLocationPickerExpanded && (
              <div className="mt-2">
                <LocationListPickerV3
                  key={selectedLocationId || "no-location"}
                  items={cityGroups}
                  multiSelect={false}
                  autoSelectSingleLocation={true}
                  initialSelectedIds={currentLocationInList?.id ? [currentLocationInList.id] : []}
                  onSelectionChange={(selectedIds) => {
                    if (selectedIds.length > 0) {
                      handleLocationSelect(selectedIds[0]);
                      setIsLocationPickerExpanded(false);
                    }
                  }}
                />
              </div>
            )}

            {isLoadingProviders ? (
              <div className="mt-6 flex justify-center">
                <div className="text-sm text-gray-500">
                  {t("bookingProviders.loadingProviders")}
                </div>
              </div>
            ) : (
              <div className="mt-2">
                <BookingProvidersList
                  providers={providers}
                  selectedProviderId={selectedProviderId}
                  onProviderSelection={handleProviderSelection}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Book Button */}
      <div className="sticky bottom-[calc(59px_+_env(safe-area-inset-bottom)-1px)] w-full bg-white p-4 desk:bottom-0">
        <Button fullWidth disabled={!selectedTime || isBooking} onClick={handleBook}>
          {isBooking ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              <span>{t("bookingProviders.booking")}</span>
            </div>
          ) : (
            t("bookingProviders.book")
          )}
        </Button>
      </div>
    </div>
  );
};

export default BookingProviders;
